import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Table, Button, Row, Col, Select } from 'antd';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import { withAppContext } from 'context/AppContext';
import { useGetHosts } from 'utils/apis/host';
import { getInvoiceList } from 'utils/apis/invoiceList';
import InvoicePdfModal from 'components/InvoiceList/InvoicePdfModal';
import { BasicSelect } from 'components/FormSelection/FormSelection';
import intl from 'react-intl-universal';
import styles from './InvoiceList.module.css';

const InvoiceList = ({ checkIsAdmin, getUserHosts }) => {
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calendarYear, setCalendarYear] = useState(moment().year());
  const [pdfModalVisible, setPdfModalVisible] = useState(false);
  const [pdfModalType, setPdfModalType] = useState('invoice');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedHostId, setSelectedHostId] = useState(null);

  const constructHostOptions = useCallback(hosts => hosts.map(host => ({ label: host.name, value: host._id })), []);

  const { data: hostOptions, isLoading: isLoadingHostOptions } = useGetHosts(constructHostOptions);

  useEffect(() => {
    if (selectedHostId != null) {
      return;
    }
    if (checkIsAdmin()) {
      if (hostOptions && hostOptions.length > 0) {
        setSelectedHostId(hostOptions[0].value);
      }
    } else {
      const userHosts = getUserHosts();
      if (userHosts && userHosts.length > 0) {
        setSelectedHostId(userHosts[0]);
      }
    }
  }, [selectedHostId, hostOptions, getUserHosts, checkIsAdmin]);

  const yearsForFetch = useMemo(() => [calendarYear], [calendarYear]);

  const fetchData = useCallback(async () => {
    if (!selectedHostId) {
      setRawData([]);
      setLoading(checkIsAdmin() ? isLoadingHostOptions : false);
      return;
    }

    setLoading(true);
    try {
      const result = await getInvoiceList({
        hostId: selectedHostId,
        years: yearsForFetch
      });
      setRawData(Array.isArray(result) ? result : []);
    } catch {
      setRawData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedHostId, yearsForFetch, isLoadingHostOptions, checkIsAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCalendarYearChange = year => {
    setCalendarYear(year);
  };

  const handleViewPdf = (record, type = 'invoice') => {
    setSelectedInvoice(record);
    setPdfModalType(type);
    setPdfModalVisible(true);
  };

  const handleClosePdfModal = () => {
    setPdfModalVisible(false);
    setSelectedInvoice(null);
    setPdfModalType('invoice');
  };

  const csvHeaders = useMemo(
    () => [
      { label: intl.get('invoiceList.table.createDate').d('Create Date'), key: 'createDate' },
      { label: intl.get('invoiceList.table.invoiceNo').d('Invoice No.'), key: 'invoiceNo' },
      { label: intl.get('invoiceList.table.bookingNo').d('Booking No.'), key: 'bookingNo' }
    ],
    []
  );

  const csvData = useMemo(
    () =>
      rawData.map(item => ({
        createDate: item.createdAt ? moment(item.createdAt).format('YYYY-MM-DD HH:mm') : '',
        invoiceNo: item.invoiceNo || '',
        bookingNo: item.bookingNo || ''
      })),
    [rawData]
  );

  const csvFilename = useMemo(() => `invoice-list-${calendarYear}.csv`, [calendarYear]);

  const yearSelectOptions = useMemo(() => {
    const current = moment().year();
    const options = [];
    for (let y = current + 1; y >= current - 10; y -= 1) {
      options.push(
        <Select.Option key={y} value={y}>
          {y}
        </Select.Option>
      );
    }
    return options;
  }, []);

  const columns = [
    {
      title: intl.get('invoiceList.table.createDate').d('Create Date'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      render: value => moment(value).format('YYYY-MM-DD HH:mm')
    },
    {
      title: intl.get('invoiceList.table.invoiceNo').d('Invoice No.'),
      dataIndex: 'invoiceNo',
      key: 'invoiceNo',
      sorter: (a, b) => (a.invoiceNo || '').localeCompare(b.invoiceNo || '')
    },
    {
      title: intl.get('invoiceList.table.bookingNo').d('Booking No.'),
      dataIndex: 'bookingNo',
      key: 'bookingNo',
      sorter: (a, b) => (a.bookingNo || '').localeCompare(b.bookingNo || '')
    },
    {
      title: intl.get('invoiceList.table.viewPdf').d('View Invoice PDF'),
      key: 'actionInvoice',
      render: (_, record) => (
        <Button type="link" size="small" onClick={() => handleViewPdf(record, 'invoice')}>
          {intl.get('invoiceList.table.viewPdf').d('View Invoice PDF')}
        </Button>
      )
    },
    {
      title: intl.get('invoiceList.table.viewReceiptPdf').d('View Receipt PDF'),
      key: 'actionReceipt',
      render: (_, record) => (
        <Button type="link" size="small" onClick={() => handleViewPdf(record, 'receipt')}>
          {intl.get('invoiceList.table.viewReceiptPdf').d('View Receipt PDF')}
        </Button>
      )
    }
  ];

  const showHostLoading = isLoadingHostOptions || !hostOptions;

  return (
    <div className={styles.container}>
      <Card className={styles.card}>
        <div className={styles.header}>
          <Row gutter={16} align="middle" justify="space-between" type="flex">
            <Col>
              <h2 className={styles.title}>{intl.get('invoiceList.title').d('Invoice List')}</h2>
            </Col>
            <Col>
              <div className={styles.toolbar}>
                {!showHostLoading && (
                  <BasicSelect
                    label={intl.get('invoiceList.host').d('Host')}
                    selections={hostOptions}
                    value={selectedHostId}
                    onChange={setSelectedHostId}
                    placeholder={intl.get('invoiceList.hostPlaceholder').d('Select host')}
                  />
                )}
                <span className={styles.filterLabel}>{intl.get('invoiceList.year').d('Year')}:</span>
                <Select value={calendarYear} onChange={handleCalendarYearChange} style={{ minWidth: 96 }} disabled={loading}>
                  {yearSelectOptions}
                </Select>
                {!loading && rawData.length > 0 ? (
                  <CSVLink filename={csvFilename} data={csvData} headers={csvHeaders}>
                    <Button icon="download">{intl.get('invoiceList.downloadCsv').d('Download CSV')}</Button>
                  </CSVLink>
                ) : (
                  <Button icon="download" disabled={loading || rawData.length === 0}>
                    {intl.get('invoiceList.downloadCsv').d('Download CSV')}
                  </Button>
                )}
              </div>
            </Col>
          </Row>
        </div>
        <Table
          loading={loading || showHostLoading}
          columns={columns}
          dataSource={rawData}
          rowKey={record => record._id}
          pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}` }}
        />
      </Card>
      {pdfModalVisible && (
        <InvoicePdfModal
          invoice={selectedInvoice}
          visible={pdfModalVisible}
          handleClose={handleClosePdfModal}
          host={selectedHostId}
          pdfType={pdfModalType}
        />
      )}
    </div>
  );
};

export default withAppContext(InvoiceList);
